import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Users, Play, Copy, Check, RotateCcw, Save, Sparkles, Send, Dices, AlertTriangle } from 'lucide-react';
import { marked } from 'marked';
import { Button } from '@components/ui/Button';
import { Input, TextArea, FormField } from '@components/ui';
import { useStudioTeam } from '@hooks/useStudioTeam';
import { useStudioTeamV2 } from '@hooks/useStudioTeamV2';
import { useSongTopics } from '@hooks/useSongTopics';
import { copyToClipboard } from '@/utils/clipboard';
import { fetchWithCsrf } from '@/utils/fetchWithCsrf';
import { extractBlocks, parseTitleArtist, parseStudioTeamOutput, saveStudioTeamToVault } from '@/utils/studioTeamBlocks';
import { applyLockedPersonaStyle, buildPersonaLockConstraintText, extractSongBpmKey } from '@/shared/personaLock';
import { buildSunoInjectionScript } from '@services/llmShared';
import { CopyButton } from '@components/CopyButton';
import type { SunoPrepPayload } from '@components/SunoPrep/SunoPrepModal';
import type { Persona } from '@/types/persona.types';

/**
 * Pull Pat's final 6 Suno blocks out of the full agent walkthrough.
 *
 * Each agent (Aria, Echo, Riff, Ink, Blackrose, Vera, Vox, Fader, Nix)
 * wraps their deliverables in code blocks too, so we can't just grab the
 * first 6 fenced blocks. Instead we look for "BLOCK N" / "Block N:" /
 * "### BLOCK N" headers and take the code block that follows each. If
 * the same block number appears more than once, the LAST occurrence wins
 * (that's Pat's compiled final, not an earlier draft).
 */

interface StudioTeamPanelProps {
  openSunoPrep?: (payload?: Partial<SunoPrepPayload>) => void;
  activePersona?: Persona | null;
}

export const StudioTeamPanel: React.FC<StudioTeamPanelProps> = ({ openSunoPrep, activePersona }) => {
  // Forgejo #96 Phase 3: both pipelines' hooks run unconditionally (rules of
  // hooks), the toggle just picks which one's state feeds the render. v2 is
  // the experimental per-agent pipeline -- cheaper and more validated, but
  // measured 5-10x slower (sequential round-trips vs. one continuous
  // stream) and still finding new spec gaps on real input.
  const [useV2, setUseV2] = useState(false);
  const v1 = useStudioTeam();
  const v2 = useStudioTeamV2();
  const { result, brief, loading, error, generate, reset } = useV2 ? v2 : v1;
  const { drawTopic, loading: drawingTopic } = useSongTopics();
  const [title, setTitle] = useState(brief?.title ?? '');
  const [genre, setGenre] = useState(brief?.genre ?? '');
  const [mood, setMood] = useState(brief?.mood ?? '');
  const [setting, setSetting] = useState(brief?.setting ?? '');
  const [vocal, setVocal] = useState(brief?.vocal ?? '');
  const [constraints, setConstraints] = useState(brief?.constraints ?? '');
  const [subject, setSubject] = useState(brief?.subject ?? '');
  const [subjectCategory, setSubjectCategory] = useState<string | null>(null);
  const [includeImagePrompt, setIncludeImagePrompt] = useState(brief?.includeImagePrompt ?? false);
  const [styleMode, setStyleMode] = useState<'studio' | 'live'>(brief?.styleMode ?? 'studio');
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedDraftId, setSavedDraftId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const blocks = useMemo(() => {
    const raw = result ? extractBlocks(result.text) : [];
    if (!activePersona?.useLockedTemplate) return raw;
    return raw.map(b => b.label === 'Style'
      ? { ...b, body: applyLockedPersonaStyle({ style: b.body }, activePersona, extractSongBpmKey(b.body)).style }
      : b);
  }, [result, activePersona]);
  const fullHtml = useMemo(() => result ? marked.parse(result.text) as string : '', [result]);
  const canRun = title.trim() && genre.trim() && !loading;

  // Sync local form state when a new brief arrives (e.g., "Send to Studio Team" from RiffWriter).
  // Skip the initial mount — useState already seeded from brief in that case.
  const briefRef = useRef(brief);
  useEffect(() => {
    if (briefRef.current === brief) return;
    briefRef.current = brief;
    if (!brief) return;
    setTitle(brief.title ?? '');
    setGenre(brief.genre ?? '');
    setMood(brief.mood ?? '');
    setSetting(brief.setting ?? '');
    setVocal(brief.vocal ?? '');
    setConstraints(brief.constraints ?? '');
    setSubject(brief.subject ?? '');
    setIncludeImagePrompt(brief.includeImagePrompt ?? false);
    setStyleMode(brief.styleMode ?? 'studio');
  }, [brief]);

  /**
   * Locked-persona constraint text, appended to whatever's in the
   * constraints textarea at generate time. This is the soft half of
   * persona locking (prompt influence, same "preserve this" pattern as
   * production architecture, wording shared with the server-side
   * Roulette/Album executors via buildPersonaLockConstraintText) -- the
   * hard half (deterministic Style substitution) happens after generation,
   * see `blocks` above and `handleSaveToVault` below.
   */
  const buildConstraintsWithPersonaLock = (baseConstraints: string): string | undefined => {
    const lockText = buildPersonaLockConstraintText(activePersona);
    if (!lockText) return baseConstraints || undefined;
    return [baseConstraints, lockText].filter(Boolean).join('\n\n');
  };

  const handleDrawTopic = async () => {
    const topic = await drawTopic([], genre || undefined);
    if (!topic) return;
    setSubject(topic.prompt);
    setSubjectCategory(topic.category);
  };

  // Auto-save the moment a fresh result lands. Avoids the "I ran Studio
  // Team, looked at the output, switched tabs, lost the song" failure mode.
  // Tracks the result.text identity so re-renders don't re-fire the save,
  // and resets savedDraftId on a new run so the UI doesn't claim the old
  // run is still saved.
  const autoSavedTextRef = useRef<string | null>(null);
  useEffect(() => {
    if (!result || !result.text) return;
    if (autoSavedTextRef.current === result.text) return;
    autoSavedTextRef.current = result.text;
    setSavedDraftId(null);
    void handleSaveToVault();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  /** Pull each labeled block by name. */
  const blockBy = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of blocks) map.set(b.label, b.body);
    return map;
  }, [blocks]);

  const handleCopy = async (text: string, idx: number) => {
    await copyToClipboard(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1500);
  };

  const handleSaveToVault = async (): Promise<string | null> => {
    if (!result) return null;
    setSaving(true);
    setSaveError(null);
    try {
      const parsedRaw = parseStudioTeamOutput(result.text, title || 'Untitled Studio Team Song');
      const parsed = applyLockedPersonaStyle(parsedRaw, activePersona, extractSongBpmKey(parsedRaw.style));
      const id = await saveStudioTeamToVault(parsed, result.text, {
        provider: result.provider,
        model: result.model,
        genre,
        brief: { title, genre, mood, setting, vocal, constraints, subject, styleMode },
      });
      setSavedDraftId(id);
      return id;
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
      return null;
    } finally {
      setSaving(false);
    }
  };

  /** Build a ParsedSunoOutput shape from Pat's blocks so downstream tabs (Output, Art, Suno-Sync) treat this like a normal generation. */
  const buildParsedOutput = () => {
    const lyrics = blockBy.get('Lyrics') ?? '';
    const style = blockBy.get('Style') ?? '';
    const excludedStyle = blockBy.get('Excluded Style') ?? '';
    const summary = blockBy.get('Summary') ?? '';
    const titleArtist = blockBy.get('Title / Artist') ?? '';
    const { title: parsedTitle, artist: parsedArtist } = parseTitleArtist(titleArtist);
    const finalTitle = parsedTitle || title || 'Untitled Studio Team Song';
    // Strip section tags + symbols to produce a singable lyricsAlone version
    const lyricsAlone = lyrics
      .replace(/\[[^\]]*\]/g, '')
      .replace(/\([^)]{0,40}\)/g, '')
      .replace(/\/\/\/\*+\/\/\//g, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    return {
      titleArtist,
      summary,
      styleBlueprint: '',
      style,
      excludedStyle,
      title: finalTitle,
      artist: parsedArtist || '',
      lyricsWithTags: lyrics,
      lyricsAlone,
      // Studio Team has no styleStrength/weirdness sliders -- use the same
      // defaults parseResponse() falls back to for single-song generation.
      javascriptCode: buildSunoInjectionScript(finalTitle, style, lyrics, 65, 85),
      fullResponse: result?.text ?? '',
      llmModel: result?.model,
      llmProvider: result?.provider,
    };
  };

  /** Save to Vault (if not already), push the parsed output into App state, switch to Art tab. */
  const handleSendToArt = async () => {
    const id = savedDraftId ?? await handleSaveToVault();
    if (!id) return;
    const parsedOutput = buildParsedOutput();
    window.dispatchEvent(new CustomEvent('hookhouse:load-output', {
      detail: { output: parsedOutput, targetTab: 'image', draftId: id },
    }));
  };

  /** Save to Vault (if not already), then open SunoPrep pre-filled from Pat's blocks. */
  const handleSendToSuno = async () => {
    if (!openSunoPrep) return;
    const id = savedDraftId ?? await handleSaveToVault();
    const parsed = buildParsedOutput();
    openSunoPrep({
      title: parsed.title,
      tags: parsed.style,
      negativeTags: parsed.excludedStyle,
      lyrics: parsed.lyricsWithTags,
      draftId: id,
    });
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <header className="text-center">
        <div className="inline-flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-accent text-slate-950 flex items-center justify-center">
            <Users size={20} />
          </div>
          <h1 className="text-3xl font-black text-slate-50 uppercase tracking-tight">
            Studio <span className="text-accent">Team</span>
          </h1>
        </div>
        <p className="text-sm text-slate-400 max-w-2xl mx-auto">
          The full 11-agent Songwriter's Room (ARIA → ECHO → PHI → RIFF → INK → BLACKROSE → VERA → VOX → FADER → NIX → PAT),
          plus STRATA &amp; DEX when the brief calls for them.{' '}
          {useV2
            ? 'v2: one Anthropic call per agent, each independently validated.'
            : 'One call, returns the complete 6-block Suno package.'}
        </p>
      </header>

      {!result && (
        <div
          className="flex flex-wrap items-center justify-center gap-3 rounded-xl border border-slate-800 bg-slate-900/40 px-4 py-3"
          title="Forgejo #96: v2 runs each agent as its own validated, cached API call instead of one long conversation — cheaper per token and catches spec violations the old pipeline can't, but measured 5-10x slower (sequential round-trips vs. one continuous stream)."
        >
          <span className={`text-xs font-bold uppercase tracking-widest ${!useV2 ? 'text-accent' : 'text-slate-500'}`}>
            v1 · single call · 2–4 min
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={useV2}
            onClick={() => setUseV2((v) => !v)}
            className="relative w-11 h-6 rounded-full bg-slate-800 border border-slate-700 transition-colors"
          >
            <span
              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-accent transition-transform ${
                useV2 ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
          <span className={`text-xs font-bold uppercase tracking-widest ${useV2 ? 'text-accent' : 'text-slate-500'}`}>
            v2 · per-agent · experimental · ~20–30 min
          </span>
        </div>
      )}

      {!result && (
        <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6 space-y-4">
          <h2 className="text-sm font-bold uppercase tracking-widest text-slate-300">Song Brief</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Title (required)">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Devil in the Drawbar"
              />
            </FormField>
            <FormField label="Genre (required)">
              <Input
                value={genre}
                onChange={(e) => setGenre(e.target.value)}
                placeholder="e.g. Southern boogie metal, Capricorn '70s"
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Mood (optional)">
              <Input
                value={mood}
                onChange={(e) => setMood(e.target.value)}
                placeholder="dark, defiant, weary..."
              />
            </FormField>
            <FormField label="Setting (optional)">
              <Input
                value={setting}
                onChange={(e) => setSetting(e.target.value)}
                placeholder="Middle Georgia roadhouse, 1974..."
              />
            </FormField>
          </div>

          <FormField label="Vocal (optional)">
            <Input
              value={vocal}
              onChange={(e) => setVocal(e.target.value)}
              placeholder="raspy baritone with controlled grit, conversational"
            />
          </FormField>

          <FormField label="Subject (optional)">
            <div className="flex items-center gap-2">
              <Input
                value={subject}
                onChange={(e) => { setSubject(e.target.value); setSubjectCategory(null); }}
                placeholder="e.g. Losing someone you love — anchors the title/lyrics to a real subject"
                className="flex-1"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleDrawTopic}
                isLoading={drawingTopic}
                leftIcon={<Dices size={14} />}
                title="Draw a subject from the topic bank"
              >
                Draw a Topic
              </Button>
            </div>
            {subjectCategory && (
              <p className="text-[11px] text-slate-500 mt-1.5">{subjectCategory}</p>
            )}
          </FormField>

          <FormField label="Constraints (optional)">
            <TextArea
              value={constraints}
              onChange={(e) => setConstraints(e.target.value)}
              placeholder="BPM, key, time signature, length, themes to avoid..."
              rows={2}
            />
          </FormField>

          <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              checked={includeImagePrompt}
              onChange={(e) => setIncludeImagePrompt(e.target.checked)}
              className="w-4 h-4 accent-accent cursor-pointer"
            />
            Include Block 6 (Image Prompt JSON) — skip if using the Art tab instead.
          </label>

          <div
            className="flex items-center gap-2"
            title='Studio forbids "live" language in style tags entirely (Suno reads any mention of "live" as a request for concert/crowd atmosphere, even innocuous phrasing like "live room feel"). Live explicitly wants that concert character.'
          >
            <span className="text-[11px] font-black uppercase tracking-widest text-slate-500">Style</span>
            <div className="flex bg-slate-800 border border-slate-700 rounded-full p-0.5">
              {(['studio', 'live'] as const).map(mode => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setStyleMode(mode)}
                  className={`px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest transition-all ${
                    styleMode === mode ? 'bg-accent text-slate-950' : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  {mode}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300">
              {error}
            </div>
          )}

          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={() => generate({
              title, genre,
              mood: mood || undefined,
              setting: setting || undefined,
              vocal: vocal || undefined,
              constraints: buildConstraintsWithPersonaLock(constraints),
              subject: subject || undefined,
              includeImagePrompt,
              styleMode,
            })}
            disabled={!canRun}
            isLoading={loading}
            loadingText={
              useV2
                ? 'Running v2 pipeline... ~20–30 min, one call per agent — don\'t refresh'
                : "Running 11-agent pipeline... 2–4 min — don't refresh"
            }
            leftIcon={<Play size={16} />}
          >
            Run Studio Team
          </Button>

          {useV2 && loading && v2.steps.length > 0 && (
            <div className="rounded-lg border border-slate-700/60 bg-slate-950/60 p-3 text-xs font-mono max-h-48 overflow-y-auto space-y-1">
              {v2.steps.map((s, i) => (
                <div key={i} className={s.ok ? 'text-green-400' : 'text-amber-400'}>
                  {s.agent}
                  {s.ok
                    ? ` — ${s.action ?? 'ok'}`
                    : ` — retry ${s.retries} (${s.errors[0] ?? 'invalid patch'})`}
                </div>
              ))}
            </div>
          )}

          <p className="text-xs text-slate-500 text-center">
            {useV2
              ? 'Uses your Claude key from Settings -- v2 has no Gemini fallback yet. Cheaper per token and independently validates each agent\'s output, but is measured 5-10x slower than v1.'
              : <>Uses your Claude key if set in Settings (recommended — spec designed for Claude),
                otherwise falls back to Gemini. Output is raw markdown, not the RiffWriter JSON schema.</>}
          </p>
        </section>
      )}

      {result && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-700/60 bg-slate-900/40 px-4 py-3">
            <div className="text-xs text-slate-400">
              <span className="text-accent font-bold">{result.provider.toUpperCase()}</span>
              {' · '}
              <span className="font-mono">{result.model}</span>
              {result.usage && (
                <>
                  {' · '}
                  in: {result.usage.inputTokens.toLocaleString()} tok
                  {' · '}
                  out: {result.usage.outputTokens.toLocaleString()} tok
                </>
              )}
              {savedDraftId && <span className="ml-2 text-green-400">✓ Saved to Vault</span>}
              {saveError && <span className="ml-2 text-red-400">{saveError}</span>}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={savedDraftId ? 'ghost' : 'primary'}
                size="sm"
                leftIcon={savedDraftId ? <Check size={14} /> : <Save size={14} />}
                onClick={handleSaveToVault}
                isLoading={saving}
                loadingText="Saving..."
                disabled={blocks.length === 0}
              >
                {savedDraftId ? 'Saved' : 'Save to Vault'}
              </Button>
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Sparkles size={14} />}
                onClick={handleSendToArt}
                isLoading={saving}
                disabled={blocks.length === 0}
              >
                Send to Art
              </Button>
              {openSunoPrep && (
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<Send size={14} />}
                  onClick={handleSendToSuno}
                  isLoading={saving}
                  disabled={blocks.length === 0}
                  title="Save to Vault then open Suno Prep pre-filled"
                >
                  Send to Suno
                </Button>
              )}
              {blocks.length > 0 && (
                <span title="Copy the Suno browser-console script for this song (also renews a timed-out captcha)">
                  <CopyButton textToCopy={buildParsedOutput().javascriptCode} />
                </span>
              )}
              <Button variant="ghost" size="sm" leftIcon={<RotateCcw size={14} />} onClick={reset}>
                New Run
              </Button>
            </div>
          </div>

          {blocks.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-sm font-bold uppercase tracking-widest text-slate-300">
                Suno Output Blocks
              </h2>
              {blocks.map((b, idx) => {
                const isStyle = b.label === 'Style';
                const isExcludes = b.label === 'Excluded Style';
                const isLyrics = b.label === 'Lyrics';
                const len = b.body.length;
                let badge: React.ReactNode = null;
                if (isStyle) {
                  const ok = len <= 950;
                  const warn = len > 950 && len <= 999;
                  badge = (
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                      ok ? 'bg-green-500/15 text-green-300' :
                      warn ? 'bg-amber-500/15 text-amber-300' :
                      'bg-red-500/15 text-red-300'
                    }`}>
                      {len}/950
                      {result.styleCharsOriginal && result.styleCharsOriginal !== result.styleCharsFinal &&
                        ` (was ${result.styleCharsOriginal})`
                      }
                    </span>
                  );
                } else if (isExcludes) {
                  const ok = len <= 950;
                  const warn = len > 950 && len <= 999;
                  badge = (
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                      ok ? 'bg-green-500/15 text-green-300' :
                      warn ? 'bg-amber-500/15 text-amber-300' :
                      'bg-red-500/15 text-red-300'
                    }`}>
                      {len}/950
                      {result.excludesCharsOriginal && result.excludesCharsOriginal !== result.excludesCharsFinal &&
                        ` (was ${result.excludesCharsOriginal})`
                      }
                    </span>
                  );
                } else if (isLyrics) {
                  const ok = len >= 4850 && len <= 4920;
                  const close = len >= 4500 && len <= 5000;
                  badge = (
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                      ok ? 'bg-green-500/15 text-green-300' :
                      close ? 'bg-amber-500/15 text-amber-300' :
                      'bg-red-500/15 text-red-300'
                    }`}>
                      {len.toLocaleString()}/4,850–4,920
                      {result.lyricsTrimmed && result.lyricsCharsOriginal && result.lyricsCharsOriginal !== result.lyricsCharsFinal &&
                        ` (was ${result.lyricsCharsOriginal.toLocaleString()})`
                      }
                      {result.lyricsHardTrimmed && ' · hard-trimmed'}
                    </span>
                  );
                }
                return (
                  <div key={idx} className="rounded-xl border border-slate-700/60 bg-slate-900/40">
                    <div className="flex items-center justify-between px-4 py-2 border-b border-slate-800">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold uppercase tracking-widest text-accent">
                          Block {idx + 1}: {b.label}
                        </span>
                        {badge}
                      </div>
                      <button
                        onClick={() => handleCopy(b.body, idx)}
                        className="text-xs text-slate-400 hover:text-accent flex items-center gap-1.5"
                      >
                        {copiedIdx === idx ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                      </button>
                    </div>
                    {isLyrics && result.bannedLanguageRemaining && result.bannedLanguageRemaining.length > 0 && (
                      <div className="flex items-start gap-2 px-4 py-2 border-b border-amber-500/20 bg-amber-500/10 text-[11px] text-amber-200">
                        <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                        <div>
                          <div className="font-bold">Still contains banned language after auto-repair — fix before saving or sending to Suno:</div>
                          <ul className="list-disc pl-4 mt-1 space-y-0.5">
                            {result.bannedLanguageRemaining.map((v, i) => <li key={i}>{v}</li>)}
                          </ul>
                        </div>
                      </div>
                    )}
                    <pre className="p-4 text-xs text-slate-200 whitespace-pre-wrap font-mono overflow-x-auto max-h-96 overflow-y-auto">
                      {b.body}
                    </pre>
                  </div>
                );
              })}
            </section>
          )}

          {/* v2 discards each agent's reasoning by design (song_state.py's I4
              principle) -- there's no prose walkthrough to show, only the
              same 6 final blocks already rendered above. Also avoids feeding
              the synthesized sentinel markers (<<<SUNO_BLOCK_...>>>) through
              dangerouslySetInnerHTML, which they were never meant for. */}
          {!useV2 && (
            <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
              <h2 className="text-sm font-bold uppercase tracking-widest text-slate-300 mb-4">
                Full Agent Walkthrough
              </h2>
              <div
                className="prose prose-invert prose-sm max-w-none prose-headings:text-accent prose-strong:text-slate-100 prose-code:text-amber-300 prose-code:bg-slate-800/80 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-pre:bg-slate-950 prose-pre:border prose-pre:border-slate-800"
                dangerouslySetInnerHTML={{ __html: fullHtml }}
              />
            </section>
          )}
        </>
      )}
    </div>
  );
};
